import { supabase } from "@/integrations/supabase/client";
import type { RealtimeChannel } from "@supabase/supabase-js";

export type SignalKind = "offer" | "answer" | "ice" | "join" | "control";

export interface SignalMessage {
  kind: SignalKind;
  serverId: string;
  from: string;
  to?: string;
  payload: Record<string, unknown>;
}

export interface SignalingClientOptions {
  serverId: string;
  peerId: string;
  role: "host" | "client";
  playerName: string;
  onSignal: (message: SignalMessage) => void;
  onPeersChange?: (peerIds: string[]) => void;
}

export interface SignalingClient {
  channel: RealtimeChannel;
  send: (message: Omit<SignalMessage, "serverId" | "from">) => Promise<void>;
  close: () => void;
}

function getPeerIds(channel: RealtimeChannel) {
  const state = channel.presenceState();
  return Object.keys(state);
}

export function createSignalingClient(options: SignalingClientOptions): SignalingClient {
  const channel = supabase.channel(`cortex:signal:${options.serverId}`, {
    config: {
      presence: {
        key: options.peerId,
      },
    },
  });

  channel
    .on("broadcast", { event: "signal" }, ({ payload }) => {
      const message = payload as SignalMessage;
      if (message.from === options.peerId) return;
      if (message.to && message.to !== options.peerId) return;
      options.onSignal(message);
    })
    .on("presence", { event: "sync" }, () => {
      options.onPeersChange?.(getPeerIds(channel));
    })
    .subscribe(async (status) => {
      if (status === "SUBSCRIBED") {
        await channel.track({
          peerId: options.peerId,
          role: options.role,
          playerName: options.playerName,
          onlineAt: new Date().toISOString(),
        });
      }
    });

  return {
    channel,
    send: async (message) => {
      await channel.send({
        type: "broadcast",
        event: "signal",
        payload: {
          serverId: options.serverId,
          from: options.peerId,
          ...message,
        } satisfies SignalMessage,
      });
    },
    close: () => {
      supabase.removeChannel(channel);
    },
  };
}